import React from "react";
import { useController, useFormContext } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./PhoneInputField.scss";
import Label from "./Label";

interface PhoneInputFieldProps {
  label: string;
  formName: string;
  ismandatory?: boolean;
  placeholder?: string;
  popover?: string;
  disabled?: boolean;
}

const PhoneInputField: React.FC<PhoneInputFieldProps> = ({
  label,
  formName,
  ismandatory = false,
  placeholder = "",
  popover,
  disabled = false,
}) => {
  const { control } = useFormContext();
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    name: formName,
    control,
  });

  /**
   * Normalise le numéro de téléphone pour l'affichage dans PhoneInput
   * Convertit les numéros français au format national (0144114411) en format international (33144114411)
   * PhoneInput attend un numéro sans le "+" : "33144114411"
   */
  const normalizePhoneForDisplay = (phoneValue: string | undefined): string => {
    if (!phoneValue) {
      return "";
    }
    
    // Retirer les espaces
    const normalized = phoneValue.trim().replace(/\s/g, "");
    
    // Si le numéro commence par +33, retirer le +
    if (normalized.startsWith("+33")) {
      return normalized.substring(1); // "33144114411"
    }
    
    // Si le numéro commence par +, retirer le +
    if (normalized.startsWith("+")) {
      return normalized.substring(1);
    }
    
    // Si le numéro commence par 0 (format national français), convertir en international
    if (normalized.startsWith("0")) {
      return "33" + normalized.substring(1); // "33144114411"
    }
    
    return normalized;
  };

  return (
    <div className="phone-input-field">
      <Label 
        label={label}
        ismandatory={ismandatory}
        popover={popover}
        htmlFor={formName}
      />
      <PhoneInput
        country="fr"
        value={normalizePhoneForDisplay(value)}
        onChange={(phone: string) => {
          const fullPhone = "+" + phone;
          onChange(fullPhone);
        }}
        disabled={disabled}
        placeholder={placeholder}
        enableSearch={true}
        searchPlaceholder="Rechercher un pays..."
        containerClass="phone-input-container"
        inputClass={`form-control ${error ? "is-invalid" : ""}`}
        buttonClass="phone-input-button"
        dropdownClass="phone-input-dropdown"
        searchClass="phone-input-search"
        preferredCountries={["fr", "be", "ch", "ca"]}
        localization={{
          France: "France",
          Belgium: "Belgique",
          Switzerland: "Suisse",
          Canada: "Canada",
        }}
      />
      {error && <div className="invalid-feedback d-block">{error.message}</div>}
    </div>
  );
};

export default PhoneInputField;
